/* eslint-disable no-mixed-spaces-and-tabs */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { collection, onSnapshot, query, where } from 'firebase/firestore';
import { firestore } from '@/utils/firebase';
import Fuse from 'fuse.js';

const useGetAIMessages = ({ dealId = '' }) => {
	const [messages, setMessages] = useState([]);
	const [fuse, setFuse] = useState(null);
	const [isLoading, setIsLoading] = useState(true);
	const [isFetching, setIsFetching] = useState(true);
	const [isUninitialized, setIsUninitialized] = useState(true);
	const [isError, setIsError] = useState(false);

	const isSuccess = useMemo(
		() => !isError && !isUninitialized && !isLoading && !isFetching,
		[isError, isLoading, isUninitialized, isFetching],
	);

	const reset = useCallback(() => {
		setMessages([]);
		setFuse(null);
		setIsLoading(false);
		setIsFetching(false);
		setIsUninitialized(true);
		setIsError(false);
	}, []);

	useEffect(() => {
		if (!dealId) {
			reset();
			return undefined;
		}
		setIsUninitialized(false);
		const collectionRef = collection(firestore, 'aiMessages');
		const q = query(collectionRef, where('dealId', '==', dealId));

		const unsubscribe = onSnapshot(q, (snapshot) => {
			try {
				setIsFetching(true);
				setMessages((prev) => {
					let updated = [...prev];

					snapshot.docChanges().forEach((change) => {
						const doc = change.doc.data();
						const id = change.doc.id;

						const feature = {
							id,
							...doc,
							createdWhen: doc.dateCreated.toDate(),
							createdWhenMillis: doc.dateCreated.toDate().getTime(),
						};

						if (change.type === 'added' || change.type === 'modified') {
							const index = updated.findIndex((f) => f.id === id);
							if (index !== -1) updated[index] = feature;
							else updated.push(feature);
						} else if (change.type === 'removed') {
							updated = updated.filter((f) => f.id !== id);
						}
					});

					setFuse(
						new Fuse(updated, {
							keys: ['summaryType', 'dealId', 'emailType'],
							threshold: 0.3,
							includeScore: true,
						}),
					);

					return updated;
				});
			} catch (e) {
				console.error('Error getting aiMessages from Firebase', e);
				setIsError(true);
			} finally {
				setTimeout(() => {
					setIsFetching(false);
				}, 100);
				setIsLoading(false);
			}
		});

		return unsubscribe;
	}, [dealId]);

	const searchMessages = (text) => {
		if (!fuse || !text) return [];
		return fuse.search(text).map((r) => r.item);
	};

	const getMessages = ({ emailType, summaryType, summaryTypes = [] } = {}) => {
		let docs = [...(messages ?? []).slice()];

		if (summaryTypes.length > 0) {
			docs = docs.filter((doc) => summaryTypes.includes(doc.summaryType));
		} else if (summaryType) {
			docs = docs.filter((doc) => doc.summaryType === summaryType);
		}
		if (emailType) {
			docs = docs.filter((doc) => doc.emailType === emailType);
		}

		docs.sort((a, b) => b.createdWhen - a.createdWhen);
		return docs;
	};

	/**
	 * The deal's newest message of each summary type, by summary type. Types with no
	 * message are left out.
	 *
	 * @param {string[]} summaryTypes
	 * @returns {Object<string, Object>}
	 */
	const getLatestSummaries = (summaryTypes) =>
		Object.fromEntries(
			summaryTypes
				.map((summaryType) => [summaryType, getMessages({ summaryType })[0]])
				.filter(([, message]) => message),
		);

	return {
		getMessages,
		getLatestSummaries,
		searchMessages,
		isLoading,
		isFetching,
		isUninitialized,
		isError,
		isSuccess,
	};
};

export default useGetAIMessages;
